/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/destructuring-assignment */
import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { extend } from '@thd-nucleus/data-sources';
import { FitCompatibility } from '@thd-olt-component-react/fit-compatibility';
import { DimensionSearchField } from './DimensionSearchField';
import { StandardRefinement } from '../Refinements/StandardRefinement';
import { OnDisplayRefinement } from '../Refinements/OnDisplayRefinement';
import { PriceRangeRefinement } from '../Refinements/PriceRangeRefinement';
import { sortRefinements, isRefinementActive, extractNavParamFromClearAllURL } from '../../product-results-helpers';
import { ProductResultsContext } from '../ProductResultsContext';

const StandardDimension = ({
  dimension,
  hasSearchBar,
  mini,
  onChange,
  onDisplayData,
  enableMultiStore,
  priceRange,
  toggleSwitch,
  onMultiStoreSelectionChange,
  multiStoreSelection,
  multiFacet,
  fitCompatibility,
  parentID,
  selectedRefinements
}) => {

  const { data } = useContext(ProductResultsContext);
  const navParam = extractNavParamFromClearAllURL(data);

  const [filterInput, setFilterInput] = useState('');

  const handleSearchInputChange = (event) => {
    setFilterInput(event.target.value);
  };

  const getRefinementId = (refinement, refinementIndex) => {
    const { refinementKey } = refinement;
    return mini
      ? `${refinementKey}-${refinementIndex}-mini`
      : `${refinementKey}-${refinementIndex}`;
  };

  const { label = '', refinements = [] } = dimension || {};

  if (label.toUpperCase() === 'AVAILABILITY' && onDisplayData.isStoreDisplay === 'true') {
    return null;
  }

  const wrapperClassNames = classNames(
    'sui-max-h-[275px]',
    'sui-overflow-y-auto'
  );

  const containerClassNames = classNames(
    'sui-flex',
    'sui-flex-wrap',
    'sui-flex-col',
    'sui-gap-2',
    'sui-mb-1',
    'multi-select-refinement-secondary-drawer'
  );

  const renderRefinements = (refinementList) => {
    return refinementList
      .filter((ref) => (ref.label || '').toUpperCase().indexOf(filterInput.toUpperCase()) !== -1)
      .sort(sortRefinements)
      .map((refinement, refinementIndex) => {
        const id = getRefinementId(refinement, refinementIndex);
        const isRefinementSelected = isRefinementActive(
          refinement.refinementKey,
          selectedRefinements
        );

        if (refinement.label.toUpperCase() === 'PICK UP TODAY' && onDisplayData.isStoreDisplay === 'true') {
          return (
            <OnDisplayRefinement
              key={id}
              {...{
                dimension,
                id,
                onChange,
                refinement,
                toggleSwitch,
                onMultiStoreSelectionChange,
                multiStoreSelection,
                enableMultiStore,
                onDisplayData,
                multiFacet,
                parentID
              }}
            />
          );
        }

        return (
          <StandardRefinement
            key={id}
            {...{
              dimension,
              id,
              onChange,
              enableMultiStore,
              refinement,
              toggleSwitch,
              onMultiStoreSelectionChange,
              multiStoreSelection,
              onDisplay: onDisplayData.onDisplay,
              multiFacet,
              isRefinementSelected,
              parentID
            }}
          />
        );
      });
  };

  return (
    <>
      {!toggleSwitch && (
        <>
          {hasSearchBar && (
            <DimensionSearchField
              label={label}
              value={filterInput}
              onChange={handleSearchInputChange}
            />
          )}
          {priceRange && (
            <PriceRangeRefinement dimension={dimension} onChange={onChange} />
          )}
          {fitCompatibility && (
            <div
              role="button"
              tabIndex={0}
              aria-label="fit-compatibility"
              className="sui-mb-3"
            >
              <FitCompatibility
                type="MINI"
                drawerPosition="left"
                imageCacheEnabled
                navParam={navParam}
                data={data}
              />
            </div>
          )}
          <section className={wrapperClassNames}>
            <div
              className={containerClassNames}
              data-testid="refinement-secondary-drawer"
              onChange={onChange}
              data-instoredisplay="true"
            >
              {refinements && renderRefinements(refinements)}
            </div>
          </section>
        </>
      )}
      {toggleSwitch && (
        <div>
          {refinements && renderRefinements(refinements)}
        </div>
      )}
    </>
  );
};

StandardDimension.propTypes = {
  dimension: PropTypes.shape({
    dimensionId: PropTypes.string,
    label: PropTypes.string,
    refinements: PropTypes.arrayOf(
      PropTypes.shape({})
    )
  }).isRequired,
  hasSearchBar: PropTypes.bool,
  fitCompatibility: PropTypes.bool,
  mini: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onMultiStoreSelectionChange: PropTypes.func,
  enableMultiStore: PropTypes.bool,
  priceRange: PropTypes.bool,
  multiStoreSelection: PropTypes.arrayOf(PropTypes.string),
  onDisplayData: PropTypes.shape({
    onDisplay: PropTypes.string,
    onDisplayChange: PropTypes.func,
    isStoreDisplay: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool
    ]),
    itemCount: PropTypes.string
  }),
  toggleSwitch: PropTypes.bool,
  multiFacet: PropTypes.bool,
  selectedRefinements: PropTypes.arrayOf(PropTypes.shape({})),
  parentID: PropTypes.string
};

StandardDimension.defaultProps = {
  hasSearchBar: false,
  fitCompatibility: false,
  mini: false,
  onDisplayData: {
    onDisplay: 'false',
    onDisplayChange: null,
    isStoreDisplay: 'false',
    itemCount: null
  },
  priceRange: false,
  toggleSwitch: false,
  enableMultiStore: false,
  onMultiStoreSelectionChange: null,
  multiStoreSelection: null,
  multiFacet: false,
  selectedRefinements: null,
  parentID: ''
};

StandardDimension.displayName = 'StandardDimension';

StandardDimension.dataModel = extend(StandardRefinement);

export { StandardDimension };
